import type { FC, PropsWithChildren } from 'react';
import { useCallback, useEffect } from 'react';

import { Box, Col, Row } from '@opendoor/atoms/box';
import { Text } from '@opendoor/atoms/text';
import { Link } from 'platform/atoms';
import { useAuthContext } from 'platform/components/AuthContext';
import { useTOSContext } from 'platform/components/TOSProvider';
import { getLoginUrl } from 'platform/loginRedirect';
import { useObservability } from 'platform/observability';
import {
  getInitials,
  OpendoorHomeLink,
} from 'sellDirect/components/layout/sellExperience/NavigationBar';
import { CXA_EMAIL } from 'shared/helpers/globals';
import { ACCOUNT_URL } from 'shared/helpers/routes';
import { ErrorImage } from 'shared/helpers/svgCollection';
import { normalizePathname, searchParamsToObject } from 'shared/hooks/usePath';

type ErrorCause = 'user-error' | 'system-error';

type HubGenericErrorPageProps = PropsWithChildren<{
  reason: string;
  // Is the error expected and caused by the user (e.g. unauthenticated or not
  // authorized), or is it unexpected and caused by a bug or system issue (e.g.
  // an outage)? This is used for metrics purposes.
  causedBy: ErrorCause;
  error?: Error;
  headerContent?: string;
}>;
/**
 * Generic Error Page with automatic tracking
 */
export const GenericErrorPage: FC<HubGenericErrorPageProps> = ({
  reason,
  causedBy,
  headerContent = 'Oops, something went wrong',
  children,
}) => {
  const { trackGenericErrorPageView } = useTrackGenericErrorPageViewEvent();

  useEffect(
    () => trackGenericErrorPageView(reason, causedBy),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only recompute on explicit dependencies
    [],
  );

  return <ErrorPageContent headerContent={headerContent}>{children}</ErrorPageContent>;
};

export const ErrorPageContent: FC<Pick<HubGenericErrorPageProps, 'headerContent' | 'children'>> = ({
  headerContent = 'Oops, something went wrong',
  children,
}) => {
  const { forceHideTOSModal } = useTOSContext();

  // DO NOT REMOVE: Do not show the TOS modal on this page
  useEffect(() => {
    // set this as false on the initial render - so it doesn't show it on this page
    forceHideTOSModal(true);
    return () => {
      // allow the TOS component to be shown when we unmount
      forceHideTOSModal(false);
    };
  }, [forceHideTOSModal]);

  return (
    <Box as="main" className="min-h-dvh w-full flex-col items-center bg-od-bg-primary">
      <Row
        as="nav"
        className="sell-experience-navigation-bar absolute left-0 right-0 top-0 z-1001 h-16 items-center justify-between bg-od-bg-primary px-9 py-0 text-od-content-primary md:h-auto md:px-8 md:py-4"
      >
        <OpendoorHomeLink />
        <ErrorPageAccountAvatar />
      </Row>
      <Col className="w-full max-w-248 flex-1 items-center gap-y-8 p-8 pt-16 md:gap-y-10 md:p-12 md:pt-24 lg:p-16 lg:pt-28">
        <Box className="items-center justify-center md:hidden">
          <ErrorImage width={120} />
        </Box>
        <Box className="hidden items-center justify-center md:flex">
          <ErrorImage width={180} />
        </Box>
        <Text
          as="p"
          className="text-center text-subheader-sm text-od-content-primary md:text-header-lg"
        >
          {headerContent}
        </Text>
        {children ?? (
          <Text as="p" className="text-center text-body-md text-od-content-primary">
            It looks like we ran into an issue. Please try again.
            <br />
            Email us at{' '}
            <Link
              href={`mailto:${CXA_EMAIL}`}
              analyticsName="cake-generic-error-page-email-opendoor"
            >
              {CXA_EMAIL}
            </Link>{' '}
            if the problem persists.
          </Text>
        )}
      </Col>
    </Box>
  );
};

// Avatar that mirrors the new cake nav (initials circle linking to account).
// On the error page we render a plain Link to /account rather than the popover
// menu used by `SellExperienceNavigation` — that menu depends on
// `QualificationContext`, which isn't in scope at the error boundary.
const ErrorPageAccountAvatar: FC = () => {
  const { state, user } = useAuthContext();

  if (state === 'authenticating') {
    return null;
  }

  if (state !== 'authenticated') {
    return (
      <Link
        href={getLoginUrl(window.location.href)}
        analyticsName="cake-generic-error-page-sign-in"
        aria-label="Sign in"
      >
        Sign in
      </Link>
    );
  }

  return (
    <Link
      href={ACCOUNT_URL}
      analyticsName="cake-generic-error-page-account"
      aria-label="Go to your account"
      className="inline-flex size-10 items-center justify-center rounded-full bg-od-bg-inverse-primary text-label-md !text-od-content-inverse-primary !no-underline"
    >
      {getInitials(user?.fullName)}
    </Link>
  );
};

/**
 * Redirect to generic error route with metrics
 */
export const useTrackGenericErrorPageViewEvent = () => {
  const { trackEvent } = useObservability();
  const location = window.location;
  const normalizedPath = normalizePathname(location.pathname);

  const trackGenericErrorPageView = useCallback(
    (reason: string, causedBy: ErrorCause, data?: Record<string, unknown>) => {
      trackEvent(
        'error-page-view',
        normalizedPath,
        {
          ...data,
          team: data?.team ?? 'seller',
          reason,
          causedBy,
          path: normalizedPath,
          pathname: location.pathname,
          hash: location.hash,
          queryParams: searchParamsToObject(location.search),
        },
        { addTiming: true },
      );
    },
    // Don't put trackEvent in deps array here because the contextual data could change often.
    // Don't put location here because we already have normalizedPath.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only recompute on explicit dependencies
    [normalizedPath],
  );

  return { trackGenericErrorPageView };
};
